import React, { useEffect, useCallback, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize2 } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { mediaSrc } from "@/lib/media";

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const DOUBLE_TAP_MS = 300;

export default function Lightbox({ items, index, onClose, onPrev, onNext }) {
  const item = items[index];

  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const gestureRef = useRef({
    pinchStartDist: 0,
    pinchStartScale: 1,
    panStart: null,      // {x, y, ox, oy}
    lastTapAt: 0,
    pinching: false,
  });

  // Reset zoom whenever the image changes.
  useEffect(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, [item?.id]);

  const handleKey = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    },
    [onClose, onPrev, onNext]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  // Lock body scroll while open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const clampOffset = (s, o) => {
    // Allow panning only when zoomed in; clamp to reasonable bounds.
    const limit = Math.max(0, (s - 1) * 400);
    return {
      x: Math.max(-limit, Math.min(limit, o.x)),
      y: Math.max(-limit, Math.min(limit, o.y)),
    };
  };

  const applyZoom = (next) => {
    const clamped = Math.max(MIN_SCALE, Math.min(MAX_SCALE, next));
    setScale(clamped);
    if (clamped === 1) setOffset({ x: 0, y: 0 });
    else setOffset((o) => clampOffset(clamped, o));
  };

  // --- Touch handlers for pinch + pan + double-tap ---
  const onTouchStart = (e) => {
    const g = gestureRef.current;
    if (e.touches.length === 2) {
      g.pinching = true;
      const [a, b] = e.touches;
      g.pinchStartDist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      g.pinchStartScale = scale;
    } else if (e.touches.length === 1) {
      // Double tap?
      const now = Date.now();
      if (now - g.lastTapAt < DOUBLE_TAP_MS) {
        applyZoom(scale > 1 ? 1 : 2.5);
      }
      g.lastTapAt = now;
      if (scale > 1) {
        g.panStart = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
          ox: offset.x,
          oy: offset.y,
        };
      }
    }
  };

  const onTouchMove = (e) => {
    const g = gestureRef.current;
    if (g.pinching && e.touches.length === 2) {
      const [a, b] = e.touches;
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      if (g.pinchStartDist > 0) {
        applyZoom(g.pinchStartScale * (dist / g.pinchStartDist));
      }
      e.preventDefault();
    } else if (g.panStart && e.touches.length === 1 && scale > 1) {
      const next = {
        x: g.panStart.ox + (e.touches[0].clientX - g.panStart.x),
        y: g.panStart.oy + (e.touches[0].clientY - g.panStart.y),
      };
      setOffset(clampOffset(scale, next));
      e.preventDefault();
    }
  };

  const onTouchEnd = (e) => {
    const g = gestureRef.current;
    if (e.touches.length < 2) g.pinching = false;
    if (e.touches.length === 0) g.panStart = null;
  };

  // --- Mouse wheel zoom (desktop) ---
  const onWheel = (e) => {
    if (item?.type !== "photo") return;
    e.preventDefault();
    const delta = -e.deltaY * 0.0015;
    applyZoom(scale + delta * scale);
  };

  // Double-click on desktop
  const onDoubleClick = (e) => {
    if (item?.type !== "photo") return;
    e.stopPropagation();
    applyZoom(scale > 1 ? 1 : 2.5);
  };

  return (
    <AnimatePresence>
      {item && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] bg-noir-base/95 backdrop-blur-sm flex items-center justify-center overflow-hidden"
          data-testid="lightbox-overlay"
          onClick={onClose}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 text-champagne hover:text-pink-hot z-10 p-2"
            data-testid="lightbox-close"
            aria-label="Close"
          >
            <X className="w-7 h-7 sm:w-8 sm:h-8" />
          </button>

          {item.type === "photo" && (
            <div
              className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1 rounded-full bg-noir-elevated/85 border border-pink-hot/30 px-2 py-1 backdrop-blur-md"
              onClick={(e) => e.stopPropagation()}
              data-testid="lightbox-zoom-controls"
            >
              <button
                onClick={() => applyZoom(scale - 0.5)}
                disabled={scale <= MIN_SCALE}
                className="p-2 text-champagne hover:text-pink-hot disabled:opacity-40"
                data-testid="lightbox-zoom-out"
                aria-label="Zoom out"
              >
                <ZoomOut className="w-5 h-5" />
              </button>
              <button
                onClick={() => applyZoom(1)}
                className="px-3 text-[11px] tracking-[0.25em] uppercase text-champagne/80 hover:text-pink-hot"
                data-testid="lightbox-zoom-reset"
                aria-label="Reset zoom"
              >
                {Math.round(scale * 100)}%
              </button>
              <button
                onClick={() => applyZoom(scale + 0.5)}
                disabled={scale >= MAX_SCALE}
                className="p-2 text-champagne hover:text-pink-hot disabled:opacity-40"
                data-testid="lightbox-zoom-in"
                aria-label="Zoom in"
              >
                <ZoomIn className="w-5 h-5" />
              </button>
              <div className="hidden sm:flex items-center text-[10px] tracking-[0.25em] uppercase text-champagne/50 pl-2 pr-3 border-l border-pink-hot/20 ml-1">
                <Maximize2 className="w-3 h-3 mr-1.5" /> Pinch · Scroll · Double-tap
              </div>
            </div>
          )}

          <button
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            className="absolute left-1 sm:left-6 text-champagne hover:text-pink-hot p-3 z-10"
            data-testid="lightbox-prev"
            aria-label="Previous"
          >
            <ChevronLeft className="w-8 h-8 sm:w-10 sm:h-10" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            className="absolute right-1 sm:right-6 text-champagne hover:text-pink-hot p-3 z-10"
            data-testid="lightbox-next"
            aria-label="Next"
          >
            <ChevronRight className="w-8 h-8 sm:w-10 sm:h-10" />
          </button>

          <motion.div
            key={item.id}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.35 }}
            className="relative w-full h-full flex flex-col items-center justify-center px-2 sm:px-12"
            onClick={(e) => e.stopPropagation()}
          >
            {item.type === "photo" ? (
              <div
                className="flex items-center justify-center w-full"
                style={{ touchAction: "none" }}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                onWheel={onWheel}
                onDoubleClick={onDoubleClick}
              >
                <img
                  src={mediaSrc(item)}
                  alt={item.caption || "photo"}
                  draggable={false}
                  data-testid="lightbox-image"
                  className="max-w-[100vw] max-h-[85vh] sm:max-w-[90vw] sm:max-h-[80vh] object-contain rounded-sm select-none"
                  style={{
                    transform: `translate3d(${offset.x}px, ${offset.y}px, 0) scale(${scale})`,
                    transformOrigin: "center center",
                    transition: gestureRef.current.pinching || gestureRef.current.panStart ? "none" : "transform 0.25s ease",
                    cursor: scale > 1 ? "grab" : "zoom-in",
                    willChange: "transform",
                    boxShadow: scale === 1 ? "0 0 48px rgba(255,20,147,0.35)" : "none",
                  }}
                />
              </div>
            ) : (
              renderVideo(item)
            )}
            {item.caption && scale === 1 && (
              <div className="mt-4 text-center font-display italic text-champagne/80 text-sm sm:text-lg px-4">{item.caption}</div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function renderVideo(item) {
  const src = mediaSrc(item);
  if (item.external_url) {
    const url = item.external_url;
    let embed = url;
    const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
    if (yt) embed = `https://www.youtube.com/embed/${yt[1]}`;
    const vm = url.match(/vimeo\.com\/(\d+)/);
    if (vm) embed = `https://player.vimeo.com/video/${vm[1]}`;
    if (embed !== url) {
      return (
        <iframe
          src={embed}
          className="w-full max-w-4xl aspect-video rounded-sm"
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          title={item.caption || "video"}
        />
      );
    }
    return (
      <video src={url} controls playsInline className="max-w-full max-h-[80vh] rounded-sm" />
    );
  }
  return <video src={src} controls playsInline className="max-w-full max-h-[80vh] rounded-sm" />;
}
